import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { dateDifference } from 'lib/datetime/dateUtils'
import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const StyledClickable = styled(Clickable)`
  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`
const ImageContainer = styled.div`
  padding-bottom: 60%;
  position: relative;
`

const CampaignImage = styled(Image)`
  position: absolute;
  inset: 0;
`

const EndsInLabel = styled(UrgencyLabel)`
  position: absolute;
  top: ${rem(8)};
  inset-inline-start: ${rem(8)};
`

const CarrierContainer = styled.div`
  position: absolute;
  padding: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  bottom: ${rem(12)};
  inset-inline-start: ${rem(8)};
`

const CarrierImage = styled(Image)`
  max-width: ${rem(80)};
`

interface Props {
  title: string
  subTitle: string
  endDate: string
  logoUrl?: string
  heroImageId: string
  id: string
  price: number
  hideTime?: boolean
  hidePrice?: boolean
  fareClass?: App.FlightsFareClass
}

export function FlightCampaignsCard(props: Props) {
  const { title, endDate, subTitle, logoUrl, heroImageId, id, hideTime = false, price, hidePrice = false, fareClass } = props
  const intl = useIntl()
  const { fareCabinLabelMap } = useFlightFareLabels()

  const endDateDifference = useMemo(() => {
    const difference = dateDifference(new Date(endDate))

    if (difference.days <= 1) {
      return intl.formatMessage(
        {
          id: 'f_pM7NK3W8SWhDVGmt5U',
          defaultMessage: '{hours}H : {minutes}M : {seconds}S',
          description: 'Countdown timer showing hours, minutes and seconds remaining for a flight campaign',
        },
        {
          hours: Math.max(difference.hours, 0),
          minutes: Math.max(difference.minutes, 0),
          seconds: Math.max(difference.seconds, 0),
        },
      )
    }

    return intl.formatMessage(
      {
        id: 'LhcRTXF7CwDxuiRtRew9',
        defaultMessage: '{days, plural, one {# DAY} other {# DAYS}}',
        description: 'Countdown timer showing days remaining for a flight campaign',
      },
      { days: difference.days },
    )
  }, [endDate, intl])

  const priceCaption = fareClass
    ? intl.formatMessage({
        id: 'Yg8b2ks8a8S84ds4qCI1',
        defaultMessage: '{fareClassLabel} from',
        description: 'Price caption showing fare class label followed by "from" for flight campaign card',
      }, { fareClassLabel: fareCabinLabelMap[fareClass] })
    : intl.formatMessage({
        id: 'alqM5M3XYJaJiUCNrMgY',
        defaultMessage: 'From',
        description: 'Price caption "From" when no fare class is specified on flight campaign card',
      })

  return (
    <StyledClickable to={`/flights/campaigns/${id}`}>
      <VerticalSpacer gap={16} data-testid="offer-card">

        <ImageContainer>
          <CampaignImage image={{ id: heroImageId }} dpr={2} width={250} fit="center" aspectRatio="3:2" />

          {!hideTime && (
            <EndsInLabel variant="critical2">
              {intl.formatMessage({
                id: 'EoFoPjnY5hr52KQh8hmN',
                defaultMessage: 'OFFER ENDS IN {endDateDifference}',
                description: 'Urgency label showing time remaining for a flight campaign offer',
              }, { endDateDifference })}
            </EndsInLabel>
          )}

          {logoUrl && <CarrierContainer>
            <CarrierImage
              height={34}
              fit="center"
              image={{ url: logoUrl }}
            />
          </CarrierContainer>}
        </ImageContainer>

        <VerticalSpacer gap={12}>
          <VerticalSpacer gap={8}>
            <BodyText variant="large" weight="bold">{title}</BodyText>
            <BodyTextBlock variant="medium" startIcon={<LineTagAltIcon />}>
              {subTitle}
            </BodyTextBlock>
          </VerticalSpacer>

          <Group direction="horizontal" horizontalAlign={!hidePrice ? 'space-between' : 'end'} verticalAlign="center">
            {!hidePrice && (
              <div>
                <PriceRowPriceCaption>
                  {priceCaption}
                </PriceRowPriceCaption>

                <PriceRowPrice
                  price={price}
                  saleUnit="person"
                  size="M"
                />
              </div>
            )}

            <TextButton kind="secondary">
              {intl.formatMessage({
                id: 'A_Yr_ON0c0ophlizXdqJ',
                defaultMessage: 'View offers',
                description: 'Button text to view flight campaign offers in the flights flow',
              })}
            </TextButton>
          </Group>
        </VerticalSpacer>
      </VerticalSpacer>
    </StyledClickable>
  )
}
